import {
  type ColumnFiltersState,
  createColumnHelper,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { Download, FileText, Loader2, RefreshCw } from 'lucide-react';
import * as React from 'react';
import { useCallback, useState } from 'react';

import { useApproveManualPayments } from '~entities/payment/api/approve-manual-payments';
import { useGetPaymentDetails } from '~entities/payment/api/get-payment-details';
import { type PaymentDetailsInvoice } from '~entities/payment/modal/invoice';

import { useGetCurrency } from '~shared/api/currency-api';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { exportToCSV } from '~shared/helpers/csv';
import { capitalizeWords } from '~shared/helpers/text-formatting';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';

interface ViewReportProps {
  date: string;
  type: 'auto' | 'manual';
}

const RETRYABLE_STATUSES = new Set([
  'failed',
  'pending',
  'partially paid',
  'scheduled',
  'invoice scheduled',
]);

const columnHelper = createColumnHelper<PaymentDetailsInvoice>();

const RetryPaymentButton = ({
  invoice,
  reportDate,
}: {
  invoice: PaymentDetailsInvoice;
  reportDate: string;
}) => {
  const mutation = useApproveManualPayments();
  const status = (invoice.status || '').toLowerCase();
  const canRetry = Boolean(invoice.invoiceId) && RETRYABLE_STATUSES.has(status);

  const handleRetry = useCallback(async () => {
    if (!canRetry) return;
    try {
      await mutation.mutateAsync({
        invoiceId: [invoice.invoiceId],
        date: format(new Date(reportDate), 'yyyy-MM-dd'),
      });
    } catch (error) {
      console.error('Error retrying payment from view report:', error);
    }
  }, [canRetry, invoice.invoiceId, mutation, reportDate]);

  if (!canRetry) {
    return (
      <span className='text-xs text-muted-foreground' title={invoice.status}>
        —
      </span>
    );
  }

  return (
    <Button
      variant='outline'
      size='sm'
      onClick={() => void handleRetry()}
      disabled={mutation.isPending}
    >
      {mutation.isPending ? (
        <Loader2 className={cn('size-4 animate-spin')} />
      ) : (
        <RefreshCw className={cn('size-4')} />
      )}
      Retry Payment
    </Button>
  );
};

export const ViewReport = ({ date, type }: ViewReportProps) => {
  const { data: currencyDetails } = useGetCurrency();
  const currencySymbol = currencyDetails?.symbol ?? '$';

  const { data, isLoading, isFetching } = useGetPaymentDetails({
    date: format(new Date(date), 'yyyy-MM-dd'),
  });
  const showInitialLoader = isLoading && !data;
  const pendingCount = data?.pending?.length ?? 0;

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('studentName', {
        header: (info) => (
          <DataTableColumnHeader
            disableHiding
            className='pl-4'
            column={info.column}
            title='Student Name'
          />
        ),
        cell: (info) => (
          <div className='px-4 capitalize'>{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('productName', {
        header: (info) => (
          <DataTableColumnHeader
            disableHiding
            className='pl-4'
            column={info.column}
            title='Class / Product'
          />
        ),
        cell: (info) => (
          <div className='px-4 capitalize'>{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('amount', {
        header: (info) => (
          <DataTableColumnHeader
            disableHiding
            className='pl-4'
            column={info.column}
            title='Amount'
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('paymentMethod', {
        header: (info) => (
          <DataTableColumnHeader
            disableHiding
            className='pl-4'
            column={info.column}
            title='Payment Method'
          />
        ),
        cell: (info) => (
          <div className='px-4 capitalize'>
            {info.getValue().split('_').join(' ')}
          </div>
        ),
      }),
      columnHelper.accessor('status', {
        header: (info) => (
          <DataTableColumnHeader
            disableHiding
            className='pl-4'
            column={info.column}
            title='Status'
          />
        ),
        cell: (info) => (
          <div className='px-4 capitalize'>{info.getValue()}</div>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <div className=''>Actions</div>,
        cell: (info) => (
          <RetryPaymentButton invoice={info.row.original} reportDate={date} />
        ),
      }),
    ],
    [date],
  );

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const handleExportPaymentHistory = () => {
    const headers = [
      'Student Name',
      'Class / Product',
      'Amount',
      'Payment Method',
      'Status',
    ];

    const formattedData = data?.invoices.map((invoice) => ({
      'Student Name': capitalizeWords(invoice.studentName),
      'Class / Product': capitalizeWords(invoice.productName),
      Amount: invoice.amount,
      'Payment Method': capitalizeWords(
        invoice.paymentMethod.split('_').join(' '),
      ),
      Status: capitalizeWords(invoice.status),
    }));

    exportToCSV(
      formattedData,
      headers,
      `${type === 'auto' ? 'auto' : 'manual'}-payment-processing-history-${format(new Date(date), 'EEEE, MMMM dd, yyyy')}.csv`,
    );
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant='outline' size='sm'>
          <FileText className='size-4' />
          View Report
        </Button>
      </DialogTrigger>
      <DialogContent className='flex max-h-[90vh] max-w-4xl flex-col overflow-hidden'>
        <DialogHeader className='shrink-0'>
          <DialogTitle>Payment Report</DialogTitle>
          <DialogDescription>
            {format(new Date(date), 'EEEE, MMMM dd, yyyy')} -{' '}
            {type === 'auto' ? 'Auto' : 'Manual'} Processing
          </DialogDescription>
        </DialogHeader>
        <div className='flex-1 overflow-y-auto'>
          {showInitialLoader ? (
            <div className='flex h-32 items-center justify-center'>
              <div className='size-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary' />
            </div>
          ) : (
            <div className={isFetching ? 'opacity-70 transition-opacity' : ''}>
              <div className='my-4 grid grid-cols-1 gap-4 md:grid-cols-3'>
                <div className='rounded-lg bg-muted/30 p-4 text-center'>
                  <div className='text-2xl font-bold'>
                    {data?.totalPayments}
                  </div>
                  <div className='text-sm text-muted-foreground'>
                    Total Payments
                  </div>
                </div>
                <div className='rounded-lg bg-muted/30 p-4 text-center'>
                  <div className='text-2xl font-bold'>
                    {currencySymbol}
                    {data?.totalAmount != null
                      ? Number(data.totalAmount).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : '—'}
                  </div>
                  <div className='text-sm text-muted-foreground'>
                    Total Amount
                  </div>
                </div>
                <div className='rounded-lg bg-muted/30 p-4 text-center'>
                  <div className='text-2xl font-bold'>
                    <span className='text-green-600'>
                      {data?.successful.length ?? 0}
                    </span>
                    {pendingCount > 0 && (
                      <span className='ml-2 text-amber-600'>
                        {pendingCount}
                      </span>
                    )}
                    {(data?.failed.length ?? 0) > 0 && (
                      <span className='ml-2 text-red-600'>
                        {data?.failed.length ?? 0}
                      </span>
                    )}
                  </div>
                  <div className='text-sm text-muted-foreground'>
                    Success
                    {pendingCount > 0 && (
                      <span className='ml-2'>/ Pending</span>
                    )}
                    {(data?.failed.length ?? 0) > 0 && (
                      <span className='ml-2'>/ Failed</span>
                    )}
                  </div>
                </div>
              </div>

              <EnrollioTable
                ariaLabel='Payment report invoices'
                totalLabel='Payments'
                filterHeader={(table) => (
                  <EnrollioTableSearch
                    placeholder='Search students...'
                    value={
                      (table.getColumn('studentName')?.getFilterValue() as
                        | string
                        | undefined) ?? ''
                    }
                    onChange={(value) => {
                      table.getColumn('studentName')?.setFilterValue(value);
                    }}
                  />
                )}
                toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
                columnFilters={columnFilters}
                columnVisibility={columnVisibility}
                columns={columns}
                data={data?.invoices ?? []}
                enableRowSelection={true}
                isUpdating={isFetching}
                onColumnFiltersChange={setColumnFilters}
                onColumnVisibilityChange={setColumnVisibility}
                onRowSelectionChange={setRowSelection}
                onSortingChange={setSorting}
                rowSelection={rowSelection}
                showSelectionCount
                sorting={sorting}
              />
            </div>
          )}
        </div>
        <DialogFooter className='flex shrink-0 flex-col gap-2 sm:flex-row sm:gap-0'>
          <div className='flex gap-2 sm:mr-auto'>
            <Button
              variant='secondary'
              size='sm'
              onClick={() => {
                handleExportPaymentHistory();
              }}
            >
              <Download />
              Export CSV
            </Button>
          </div>
          <DialogClose asChild>
            <Button variant='outline'>Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
